import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { renderAsync } from "docx-preview";
import { AUTH_TOKEN_KEY } from "../../constants/auth";
import {
  clauseMatches,
  firstClauseSegment,
  normalizeLocateText,
  splitRefParts,
  type LocateResult,
  type LocateTarget,
  type ViewerHandle,
} from "../../types/viewerLocate";
import { revealLocated } from "../../utils/locateHighlight";
import { PreviewPageNav } from "./PreviewPageNav";

export interface DocxPreviewProps {
  /** Document UUID — original docx bytes are fetched from /download. */
  documentId: string;
  /**
   * Override the fetch URL. Defaults to `/download` (original docx bytes). For
   * `.doc`, the parent passes `/preview/docx` so the backend converts
   * .doc→.docx via LibreOffice before render.
   */
  fetchUrl?: string;
  onReady?: () => void;
  onError?: (err: Error) => void;
}

type RenderState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "error"; message: string };

/** Fetch transfer timeout — mirrors the upload timeout convention in
 * api/index.ts (large file transfer, not a synchronous AI call). */
const FETCH_TIMEOUT_MS = 120_000;

/** Link protocols allowed to remain clickable after render. */
const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);

/**
 * docx-preview renders untrusted OOXML into HTML with `experimental: true`
 * and has no built-in href sanitization — its README documents only the
 * rendering options above, no protocol allowlist. A crafted hyperlink field
 * can carry a `javascript:` URI that executes on click while the auth token
 * sits in localStorage. Strip anything outside the http/https/mailto
 * allowlist after render, and harden the rest against tabnabbing.
 */
function sanitizeRenderedLinks(container: HTMLElement): void {
  const anchors = container.querySelectorAll<HTMLAnchorElement>("a[href]");
  for (const a of anchors) {
    let protocol: string;
    try {
      protocol = new URL(a.href, document.baseURI).protocol;
    } catch {
      a.removeAttribute("href");
      continue;
    }
    if (!ALLOWED_LINK_PROTOCOLS.has(protocol)) {
      a.removeAttribute("href");
      continue;
    }
    a.setAttribute("rel", "noopener noreferrer");
    a.setAttribute("target", "_blank");
  }
}

/**
 * Page elements produced by docx-preview. With breakPages:true + inWrapper,
 * each page is a <section> inside `.docx-wrapper`. Used for page count and
 * page-jump navigation.
 */
function getDocxPages(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const explicit = container.querySelectorAll<HTMLElement>(".docx-wrapper > section");
  if (explicit.length) return Array.from(explicit);
  return Array.from(container.querySelectorAll<HTMLElement>("section"));
}

/**
 * Renders a .docx directly in the browser via docx-preview, bypassing the
 * LibreOffice → PDF → pdfjs pipeline entirely. Avoids LO's NotoSansCJK
 * subset-font OTS sanitizer rejection that leaves pdfjs rendering blank
 * pages even after the stopAtErrors soft-fail patch.
 *
 * docx-preview parses OOXML and emits absolutely-positioned HTML + the
 * browser's own font stack, so text selection, CJK glyphs, tables, and
 * pagination work without any server-side conversion.
 */
export default forwardRef<ViewerHandle, DocxPreviewProps>(function DocxPreview(
  { documentId, fetchUrl, onReady, onError },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<RenderState>({ status: "loading" });
  // Bumped by the retry button to force a fresh fetch+render.
  const [attempt, setAttempt] = useState(0);
  // Rendered page count + current page for the page-nav footer. docx-preview
  // pagination (breakPages) is approximate, so this is the rendered page count,
  // not the source document's page count.
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useImperativeHandle(
    ref,
    (): ViewerHandle => ({
      async locate(target: LocateTarget): Promise<LocateResult> {
        const container = containerRef.current;
        if (!container) return "not-ready";
        if (state.status !== "ready") return "not-ready";

        const blocks = Array.from(
          container.querySelectorAll<HTMLElement>(
            "p, td, li, h1, h2, h3, h4, h5, h6",
          ),
        );

        // 1. Direct page jump when the source carries a page number. docx
        // pagination is approximate (breakPages), so this jumps the Nth
        // rendered <section> rather than a faithful source page.
        if (target.page != null) {
          const pages = getDocxPages(container);
          const el = pages[target.page - 1];
          if (el) {
            revealLocated(el, "start");
            return "ok";
          }
          // No page sections rendered — fall through to text matching.
        }

        const clause = target.clauseText?.trim();

        // 2. clauseText per-block — the precise paragraph the AI quoted. This is
        // the only step that promises a sentence-level highlight; the steps
        // below degrade gracefully to a region (heading) when the AI abridged or
        // stitched the quote.
        if (clause) {
          for (const el of blocks) {
            if (clauseMatches(el.textContent ?? "", clause)) {
              revealLocated(el);
              return "ok";
            }
          }
          // 3. clauseText whole-document join — rescues cross-block abridged
          // quotes (e.g. "第十二条……第2名董事" whose surviving segments live in
          // different <p>s, so no single block matches step 2). Verify all
          // segments still appear in order across the joined document, then
          // land on the block where the FIRST segment begins.
          const firstSeg = firstClauseSegment(clause);
          if (firstSeg) {
            const joined = normalizeLocateText(
              blocks.map((b) => b.textContent ?? "").join(""),
            );
            if (clauseMatches(joined, clause)) {
              const el = blocks.find((b) =>
                normalizeLocateText(b.textContent ?? "").includes(firstSeg),
              );
              if (el) {
                revealLocated(el);
                return "ok";
              }
            }
          }
        }

        // 4. ref fallback — the section the finding is about. splitRefParts
        // turns a free-form ref ("第十六章其他规定·转让", "支付前承诺/限制") into
        // candidate headings; match the SHORTEST containing block, which biases
        // toward the heading itself rather than a body paragraph that merely
        // mentions the term. Honest best-effort region pointer for findings
        // whose clauseText was stitched from several sections or absent.
        for (const part of splitRefParts(target.ref)) {
          const matches = blocks
            .filter((b) =>
              normalizeLocateText(b.textContent ?? "").includes(part),
            )
            .sort((a, b) => (a.textContent ?? "").length - (b.textContent ?? "").length);
          if (matches.length) {
            revealLocated(matches[0]);
            return "ok";
          }
        }

        return "not-found";
      },
    }),
    [state.status],
  );

  const render = useCallback(
    async (isCancelled: () => boolean, controller: AbortController) => {
      if (!containerRef.current) return;
      setState({ status: "loading" });
      setNumPages(0);
      setCurrentPage(1);
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      // renderAsync has no cancellation hook: once called it keeps mutating
      // its target element until it settles, regardless of what happens to
      // this closure afterwards. Render into a scratch element instead of
      // containerRef so a stale (cancelled) renderAsync in flight can never
      // interleave its writes with the next attempt's writes on the live,
      // visible container.
      //
      // The scratch element is attached to document.body (visibility:hidden,
      // off-screen) rather than left fully detached: docx-preview's
      // `experimental: true` tab-stop calculation (updateTabStop /
      // computePixelToPoint in its bundle) reads getBoundingClientRect /
      // offsetWidth / getComputedStyle off the rendered nodes to align tab
      // stops — a never-inserted element reports zero-size rects there and
      // would silently mis-align tab stops instead of erroring. Keeping it
      // attached-but-hidden preserves real layout metrics while never being
      // visible or reachable through containerRef.
      const scratch = document.createElement("div");
      scratch.style.position = "absolute";
      scratch.style.visibility = "hidden";
      scratch.style.pointerEvents = "none";
      scratch.style.left = "-99999px";
      scratch.style.top = "0";
      document.body.appendChild(scratch);
      try {
        const token =
          typeof localStorage !== "undefined"
            ? (localStorage.getItem(AUTH_TOKEN_KEY) ?? "")
            : "";
        const resp = await fetch(
          fetchUrl ?? `/api/v1/jerry/documents/${documentId}/download`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            signal: controller.signal,
          },
        );
        if (isCancelled()) return;
        if (!resp.ok) {
          throw new Error(`下载失败 (HTTP ${resp.status})`);
        }
        const buffer = await resp.arrayBuffer();
        if (isCancelled()) return;
        // experimental: true enables fuller OOXML coverage (text boxes, tab
        // stops). breakPages: true splits output into per-page wrapper divs.
        await renderAsync(buffer, scratch, undefined, {
          className: "docx",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          experimental: true,
          useBase64URL: false,
        });
        if (isCancelled()) return;
        // Sanitize before the content is ever moved into the live, visible
        // container — closes the window where an unsanitized dangerous href
        // would sit in the real DOM.
        sanitizeRenderedLinks(scratch);
        const container = containerRef.current;
        if (!container || isCancelled()) return;
        // Single synchronous swap: replaces any prior ready content in one
        // step, so a retry never stacks pages and the live container is
        // never mutated except by this atomic hand-off.
        container.replaceChildren(...Array.from(scratch.childNodes));
        setNumPages(getDocxPages(container).length);
        setCurrentPage(1);
        setState({ status: "ready" });
        onReady?.();
      } catch (e) {
        if (isCancelled()) return;
        const err = e instanceof Error ? e : new Error(String(e));
        const message =
          err.name === "AbortError" ? "下载超时，请重试" : err.message;
        setState({ status: "error", message });
        onError?.(err.name === "AbortError" ? new Error(message) : err);
      } finally {
        clearTimeout(timeoutId);
        scratch.remove();
      }
    },
    [documentId, onReady, onError],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void render(() => cancelled, controller);
    return () => {
      cancelled = true;
      controller.abort();
    };
    // attempt is intentionally a dependency — bumping it re-runs the render.
  }, [render, attempt]);

  const jumpToPage = useCallback((page: number) => {
    const pages = getDocxPages(containerRef.current);
    const target = Math.max(1, Math.min(pages.length, Math.floor(page) || 1));
    const el = pages[target - 1];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setCurrentPage(target);
    }
  }, []);

  // Track the topmost visible page on scroll (rAF-throttled) so the page-nav
  // footer stays in sync as the user scrolls.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !numPages) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const pages = getDocxPages(containerRef.current);
        const top = scroller.getBoundingClientRect().top;
        let current = 1;
        for (let i = 0; i < pages.length; i += 1) {
          if (pages[i].getBoundingClientRect().top - top <= 48) {
            current = i + 1;
          } else {
            break;
          }
        }
        setCurrentPage((prev) => (prev === current ? prev : current));
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [numPages]);

  return (
    <div
      data-testid="docx-preview-root"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: "var(--jerry-gray-100)",
      }}
    >
      <div
        ref={scrollRef}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "auto",
          padding: 16,
        }}
      >
        {state.status === "loading" && (
        <div
          role="status"
          aria-live="polite"
          style={{ padding: 24, color: "var(--jerry-text-secondary)" }}
        >
          正在渲染文档…
        </div>
      )}
      {state.status === "error" && (
        <div role="alert" style={{ padding: 24, color: "#C9353F" }}>
          <div style={{ marginBottom: 8 }}>文档渲染失败：{state.message}</div>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            style={{
              padding: "4px 12px",
              cursor: "pointer",
              border: "1px solid #C9CDD4",
              background: "white",
            }}
          >
            重试
          </button>
        </div>
      )}
        <div
          ref={containerRef}
          data-testid="docx-preview-container"
        />
      </div>
      <PreviewPageNav current={currentPage} total={numPages} onJump={jumpToPage} />
    </div>
  );
});
